import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, Dialog, Stack} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckIcon from "@mui/icons-material/Check";
import {BodyText} from "../../layout/Typography";

/**
 * Progressive variant picker, ported from the VAS masterdata widget
 * (create2stay-vas .../dialogs/VariantCascade.tsx). Instead of dumping every
 * variant in one flat list, the operator narrows by one option dimension at a
 * time: pick the first option's value, then the second, etc. The order of
 * dimensions is taken from the order the keys appear in the variants' options.
 *
 * Single-value dimensions are auto-selected. If multiple variants share the
 * same visible options, the cascade falls back to a final SKU/EAN choice.
 * Picking the last option resolves immediately via onPick.
 */
export type VariantCascadeVariant = {
   id: string
   externalId: string
   sku?: string | null
   ean?: string | null
   options?: Record<string, string>
   // The variant's own resolved small-image URL, if any. Drives the per-value preview icon.
   imageUrl?: string | null
}

export const VariantCascade = ({
                                  variants,
                                  onPick
                               }: {
   variants: ReadonlyArray<VariantCascadeVariant>
   onPick: (variantExternalId: string) => void
}) => {
   const [selected, setSelected] = useState<Record<string, string>>({});
   const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

   // Stable list of option keys in the order they first appear across variants.
   const optionKeys = useMemo(() => {
      const seen: string[] = [];
      for (const variant of variants) {
         for (const key of Object.keys(optionsFor(variant))) {
            if (!seen.includes(key)) seen.push(key);
         }
      }
      return seen;
   }, [variants]);

   // Variants matching every value picked so far.
   const filtered = useMemo(() => {
      return variants.filter(variant => {
         const opts = optionsFor(variant);
         return Object.entries(selected).every(([key, val]) => opts[key] === val);
      });
   }, [variants, selected]);

   // Auto-fill any dimension that has only one possible value among the filtered variants.
   useEffect(() => {
      for (const key of optionKeys) {
         if (key in selected) continue;
         const distinct = distinctValuesForKey(filtered, key);
         if (distinct.size === 1) {
            const [only] = distinct;
            setSelected(prev => ({...prev, [key]: only}));
            return;
         }
      }
   }, [optionKeys, filtered, selected]);

   // Next key to prompt for.
   const activeKey = useMemo(() => {
      for (const key of optionKeys) {
         if (key in selected) continue;
         if (distinctValuesForKey(filtered, key).size > 1) return key;
      }
      return undefined;
   }, [optionKeys, filtered, selected]);

   // Auto-resolve once exactly one variant matches. The ref guards against double-firing.
   const resolvedRef = useRef<string | null>(null);
   const pickVariant = useCallback((variantExternalId: string) => {
      if (resolvedRef.current === variantExternalId) return;
      resolvedRef.current = variantExternalId;
      onPick(variantExternalId);
   }, [onPick]);
   const variantToPick = filtered.length === 1 ? filtered[0].externalId : null;
   useEffect(() => {
      if (variantToPick) pickVariant(variantToPick);
   }, [variantToPick, pickVariant]);

   const valuesForActiveKey = useMemo(() => {
      if (!activeKey) return [];
      return Array.from(distinctValuesForKey(filtered, activeKey));
   }, [filtered, activeKey]);

   // Representative image per value of the active dimension: the first still-matching
   // variant carrying that value which has its own resolved image.
   const imageByActiveValue = useMemo(() => {
      const map = new Map<string, string>();
      if (!activeKey) return map;
      for (const variant of filtered) {
         const val = optionsFor(variant)[activeKey];
         if (val === undefined || map.has(val) || !variant.imageUrl) continue;
         map.set(val, variant.imageUrl);
      }
      return map;
   }, [filtered, activeKey]);

   const goBack = (key: string) => {
      // Drop this key + every key after it in the cascade.
      setSelected(prev => {
         const next: Record<string, string> = {};
         const idx = optionKeys.indexOf(key);
         for (const [k, v] of Object.entries(prev)) {
            const i = optionKeys.indexOf(k);
            if (i >= 0 && i < idx) next[k] = v;
         }
         return next;
      });
   };

   const hasAmbiguousVariantMatch = !activeKey && filtered.length > 1;

   return <Stack spacing={1}>
      {/* Breadcrumb of already-picked values (click to back up to that step). */}
      {Object.keys(selected).length > 0 && (
         <Box sx={{display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center'}}>
            {optionKeys.filter(key => key in selected).map((key, idx, arr) => (
               <Box key={key} sx={{display: 'flex', gap: '6px', alignItems: 'center'}}>
                  <Box component={"button"} type={"button"} onClick={() => goBack(key)} sx={breadcrumbStyle}>
                     <BodyText type={"body2"}>{key}: <strong>{selected[key]}</strong></BodyText>
                  </Box>
                  {idx < arr.length - 1 && <ChevronRightIcon fontSize={"small"} sx={{color: '#a0a0a0'}}/>}
               </Box>
            ))}
         </Box>
      )}

      {activeKey && (
         <Stack spacing={0.5}>
            <BodyText type={"body2"} sx={{textTransform: 'capitalize', color: '#606060'}}>{activeKey}</BodyText>
            {valuesForActiveKey.map(val => {
               const img = imageByActiveValue.get(val);
               return <Box key={val} sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  {img && (
                     <Box component={"button"} type={"button"} aria-label={`Show image for ${val}`}
                          onClick={() => setLightboxSrc(img)} sx={thumbButtonStyle}>
                        <Box component={"img"} src={img} alt={""} sx={swatchStyle}/>
                     </Box>
                  )}
                  <Box component={"button"} type={"button"} sx={optionButtonStyle}
                       onClick={() => setSelected(prev => ({...prev, [activeKey]: val}))}>
                     <BodyText type={"body1"}>{val}</BodyText>
                     <ChevronRightIcon fontSize={"small"} sx={{color: '#a0a0a0'}}/>
                  </Box>
               </Box>;
            })}
         </Stack>
      )}

      {hasAmbiguousVariantMatch && (
         <Stack spacing={0.5}>
            <BodyText type={"body2"} sx={{color: '#606060'}}>{variantChoiceLabel(filtered)}</BodyText>
            {filtered.map(variant => (
               <Box key={variant.id} sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  {variant.imageUrl && (
                     <Box component={"button"} type={"button"} aria-label={`Show image for ${variantPrimaryLabel(variant)}`}
                          onClick={() => setLightboxSrc(variant.imageUrl ?? null)} sx={thumbButtonStyle}>
                        <Box component={"img"} src={variant.imageUrl} alt={""} sx={swatchStyle}/>
                     </Box>
                  )}
                  <Box component={"button"} type={"button"} sx={optionButtonStyle} onClick={() => pickVariant(variant.externalId)}>
                     <Box sx={{minWidth: 0, textAlign: 'left'}}>
                        <BodyText type={"body1"}>{variantPrimaryLabel(variant)}</BodyText>
                        {variantSecondaryLabel(variant) &&
                           <BodyText type={"body2"} sx={{color: '#909090'}}>{variantSecondaryLabel(variant)}</BodyText>}
                     </Box>
                     <CheckIcon fontSize={"small"} sx={{color: '#a0a0a0'}}/>
                  </Box>
               </Box>
            ))}
         </Stack>
      )}

      {/* Full-image preview for a value's swatch icon. */}
      <Dialog open={!!lightboxSrc} onClose={() => setLightboxSrc(null)} maxWidth={"md"}>
         {lightboxSrc && <Box component={"img"} src={lightboxSrc} alt={""}
                              sx={{display: 'block', maxWidth: '90vw', maxHeight: '80vh'}}
                              onClick={() => setLightboxSrc(null)}/>}
      </Dialog>
   </Stack>;
}

const breadcrumbStyle = {
   padding: '4px 8px',
   background: '#f0f0f0',
   border: '1px solid #d0d0d0',
   borderRadius: '4px',
   cursor: 'pointer'
} as const;

const thumbButtonStyle = {
   flexShrink: 0,
   padding: 0,
   border: '1px solid #d0d0d0',
   borderRadius: '4px',
   background: '#ffffff',
   cursor: 'zoom-in',
   lineHeight: 0
} as const;

const swatchStyle = {
   display: 'block',
   width: '36px',
   height: '36px',
   objectFit: 'cover',
   borderRadius: '4px'
} as const;

const optionButtonStyle = {
   flex: 1,
   display: 'flex',
   alignItems: 'center',
   justifyContent: 'space-between',
   gap: '12px',
   padding: '8px 10px',
   background: '#ffffff',
   border: '1px solid #d0d0d0',
   borderRadius: '4px',
   cursor: 'pointer',
   textAlign: 'left'
} as const;

const optionsFor = (variant: VariantCascadeVariant): Record<string, string> => {
   return (variant.options ?? {}) as Record<string, string>;
}

const distinctValuesForKey = (variants: ReadonlyArray<VariantCascadeVariant>, key: string): Set<string> => {
   const distinct = new Set<string>();
   for (const variant of variants) {
      const val = optionsFor(variant)[key];
      if (val !== undefined) distinct.add(val);
   }
   return distinct;
}

const variantPrimaryLabel = (variant: VariantCascadeVariant): string => {
   if (variant.ean) return `EAN ${variant.ean}`;
   if (variant.sku) return `SKU ${variant.sku}`;
   return variant.externalId;
}

const variantChoiceLabel = (variants: ReadonlyArray<VariantCascadeVariant>): string => {
   if (variants.every(variant => variant.ean)) return 'EAN';
   if (variants.every(variant => variant.sku)) return 'SKU';
   return 'Variant';
}

const variantSecondaryLabel = (variant: VariantCascadeVariant): string => {
   const parts: string[] = [];
   if (variant.sku) parts.push(`SKU ${variant.sku}`);
   if (variant.externalId && variant.externalId !== variant.ean && variant.externalId !== variant.sku) {
      parts.push(variant.externalId);
   }
   return parts.join(' · ');
}
